/**
 * Phone numbers API service (Telephony, Prompt 1.13).
 *
 * Search → order (idempotent, wallet-charged) → manage (label, agent,
 * release/reclaim). Tenant comes from the `x-tenant-id` header. Pure async
 * functions — no React state.
 */
import { get, patch, post } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type PhoneNumberStatus =
  | 'provisioning'
  | 'active'
  | 'suspended'
  | 'releasing'
  | 'released'
  | 'failed';

/** 4.3: mobile where a country's catalog enables it. */
export type PurchasableNumberType = 'local' | 'toll_free' | 'mobile';

export interface PhoneNumberCapabilities {
  voice?: boolean;
  sms?: boolean;
  mms?: boolean;
  fax?: boolean;
}

export interface PhoneNumber {
  id: string;
  e164: string;
  country: string;
  numberType: 'local' | 'toll_free' | 'mobile';
  label: string | null;
  status: PhoneNumberStatus;
  agentId: string | null;
  routingPolicyId: string | null;
  capabilities: PhoneNumberCapabilities;
  monthlyPriceUsd: number | null;
  nextRenewalAt: string | null;
  /** Set while `releasing`: the number can be reclaimed until this date. */
  releasingAt: string | null;
  releasedAt: string | null;
  /** Prompt 2.10: why it is suspended, and when the grace (then release) ends. */
  suspendedAt?: string | null;
  suspensionReason?: 'non_payment' | 'billing_suspended' | null;
  graceEndsAt?: string | null;
  /** Prompt 2.10: `tenant` (released by you) | `non_payment` (grace ended). */
  releaseReason?: 'tenant' | 'non_payment' | null;
  /** `imported` = from the tenant's own Twilio account (Prompt 2.9). */
  source?: 'purchased' | 'imported';
  /**
   * The number's voice webhook was changed in the tenant's Twilio console —
   * calls no longer reach Orlenn until it is re-applied (2.9).
   */
  webhookDrift?: { detectedAt: string; host: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableNumber {
  e164: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;
  country: string;
  capabilities: PhoneNumberCapabilities;
}

export interface NumberSearchResult {
  country: string;
  type: PurchasableNumberType;
  /** 4.3: regulated country/type — an approved compliance bundle is needed. */
  bundle?: { required: boolean; approved: boolean };
  pricing: {
    monthlyPriceUsd: number;
    perMinuteUsd: number | null;
    includedRemaining: number;
    coveredByAllotment: boolean;
    currency: 'USD';
  };
  numbers: AvailableNumber[];
}

export interface SearchNumbersQuery {
  country: string;
  type: PurchasableNumberType;
  areaCode?: string;
  nearCity?: string;
  contains?: string;
  limit?: number;
}

export interface PhoneNumberOrder {
  id: string;
  idempotencyKey: string;
  status: string;
  e164: string | null;
  country: string;
  numberType: string;
  phoneNumberId: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlaceOrderPayload {
  /** Client purchase id — the same key on retry never buys or charges twice. */
  idempotencyKey: string;
  e164: string;
  country: string;
  type: PurchasableNumberType;
  agentId?: string;
}

export interface PlaceOrderResult {
  order: PhoneNumberOrder;
  number: PhoneNumber | null;
  charge?: { chargedUsd: number; coveredByAllotment: boolean };
  /** The number was bought but could not be assigned to the agent. */
  assignmentError?: string;
}

export interface UpdatePhoneNumberPayload {
  label?: string | null;
  agentId?: string | null;
  /** Routing policy (Prompt 2.5); null = default routing. */
  routingPolicyId?: string | null;
}

export const phoneNumbersService = {
  list(includeReleased = false): Promise<PhoneNumber[]> {
    return get<PhoneNumber[]>(endpoints.phoneNumbers.list, {
      params: includeReleased ? { includeReleased: 'true' } : {},
    });
  },

  get(id: string): Promise<PhoneNumber> {
    return get<PhoneNumber>(endpoints.phoneNumbers.byId(id));
  },

  search(query: SearchNumbersQuery): Promise<NumberSearchResult> {
    const params: Record<string, string> = {
      country: query.country,
      type: query.type,
    };
    if (query.areaCode) params.areaCode = query.areaCode;
    if (query.nearCity) params.nearCity = query.nearCity;
    if (query.contains) params.contains = query.contains;
    if (query.limit) params.limit = String(query.limit);
    return get<NumberSearchResult>(endpoints.phoneNumbers.available, { params });
  },

  update(id: string, payload: UpdatePhoneNumberPayload): Promise<PhoneNumber> {
    return patch<PhoneNumber>(endpoints.phoneNumbers.byId(id), payload);
  },

  release(id: string): Promise<PhoneNumber> {
    return post<PhoneNumber>(endpoints.phoneNumbers.release(id));
  },

  reclaim(id: string): Promise<PhoneNumber> {
    return post<PhoneNumber>(endpoints.phoneNumbers.reclaim(id));
  },

  /** Pays the current month and reactivates a suspended number (402 when the balance is short). */
  reactivate(id: string): Promise<PhoneNumber> {
    return post<PhoneNumber>(endpoints.phoneNumbers.reactivate(id));
  },

  placeOrder(payload: PlaceOrderPayload): Promise<PlaceOrderResult> {
    return post<PlaceOrderResult>(endpoints.phoneNumberOrders.list, payload);
  },

  listOrders(): Promise<PhoneNumberOrder[]> {
    return get<PhoneNumberOrder[]>(endpoints.phoneNumberOrders.list);
  },
} as const;
